import {Component, OnInit} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {FrtAccessorialsMasterService} from '../../services/frt-accessorials-master/frt-accessorials-master.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {takeWhile} from 'rxjs';
import {AlertService} from '../../services/alert/alert.service';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import {RatesSroMasterService} from '../../services/rates-sro-master/rates-sro-master.service';
import AppConstants from '../../shared/common-components/AppConstants';
import {SurveryEvaluationService} from '../survey-thirtyeighty/survery-evaluation.service';
import {LoadingService} from '../../services/loading/loading.service';
import {CommonValidators} from '../../shared/common-components/common-validators';
import {Units} from '../frt-rate-query/units';
import * as _ from 'lodash';
import {StateCountryService} from '../../services/state-country/state-country.service';
import {StateCountry} from '../../shared/common-components/common-structure';
import {HhgRateQueryService} from '../../services/hhg-rate-query/hhg-rate-query.service';
import {Router} from '@angular/router';
import {filter} from 'rxjs/operators';

@Component({
    selector: 'app-frt-ontime-shipment',
    templateUrl: './frt-onetime-shipment.component.html',
    styleUrls: ['./frt-onetime-shipment.component.css'],
    standalone: false
})
export class FrtOnetimeShipmentComponent implements OnInit {
  oneTimeShipmentForm: UntypedFormGroup;
  dataSourceOrigin: Observable<any>;
  dataSourceDestination: Observable<any>;
  typeaheadDestinationLoading: boolean;
  typeaheadOriginLoading: boolean;
  private observer$ = new BehaviorSubject(true);
  accessorials: any;
  scacCodeList: any = [];
  carrierList: any;
  private defaultCountry: StateCountry;
  allCountries: StateCountry[];
  showOriginLocation: Boolean = true;
  showDestinationLocation: Boolean = true;
  showEstimatedMileage: Boolean = true;

  constructor(private fb: UntypedFormBuilder, private frtAccessorialsMasterService: FrtAccessorialsMasterService, private surveyService: SurveryEvaluationService, private ratesSroMasterService: RatesSroMasterService,
              private alertService: AlertService, private loader: LoadingService, private stateCountryService: StateCountryService,
              private hhgRateQueryService: HhgRateQueryService, private router: Router) { }

  ngOnInit() {
    this.createShipmentForm();
    this.getAllAccesorials();
    this.getScac();
    this.onValueChanges();
    this.dataSourceOrigin = Observable.create((observer: any) => {
      this.ratesSroMasterService.getLocations(this.oneTimeShipmentForm.get('asyncOriginSearchString').value)
          .subscribe((result: any) => {
            observer.next(result);
          });
    });

    this.dataSourceDestination = Observable.create((observer: any) => {
      this.ratesSroMasterService.getLocations(this.oneTimeShipmentForm.get('asyncDestinationSearchString').value)
          .subscribe((result: any) => {
            observer.next(result);
          });
    });
  }

  /**
   * @description listen to value changes of origin and destination values.
   */
  onValueChanges() {
    // setting originId on value changes
    this.oneTimeShipmentForm.get('originId').valueChanges.pipe(filter(value => value))
        .pipe(takeWhile(() => this.observer$.value))
        .subscribe((origin: any) => {
          if (AppConstants.isNotNullCondition(origin)) {
            this.oneTimeShipmentForm.get('estimatedWeight').setValue(null);
            if (this.oneTimeShipmentForm.get('destinationId').value) {
              const originZipId = origin.zipCode;
              const destinationZipId = this.oneTimeShipmentForm.get('destinationId').value.zipCode;
              this.pcMilerService(originZipId, destinationZipId, 'FRT', this.oneTimeShipmentForm.get('pickupDate').value.toISOString());
            }
            this.oneTimeShipmentForm.get('originCity').setValue(origin.cityName);
            this.oneTimeShipmentForm.get('originState').setValue(origin.stateAbbrev);
            this.oneTimeShipmentForm.get('originZipId').setValue(origin.zipCode);
          }
        });

    // setting destinationId on value changes
    this.oneTimeShipmentForm.get('destinationId').valueChanges.pipe(filter(value => value))
        .pipe(takeWhile(() => this.observer$.value))
        .subscribe((destination: any) => {
          if (AppConstants.isNotNullCondition(destination)) {
            this.oneTimeShipmentForm.get('estimatedWeight').setValue(null);
            if (this.oneTimeShipmentForm.get('originId').value) {
              const originZipId = this.oneTimeShipmentForm.get('originId').value.zipCode;
              const destinationZipId = destination.zipCode;
              this.pcMilerService(originZipId, destinationZipId, 'FRT', this.oneTimeShipmentForm.get('pickupDate').value.toISOString());
            }
            this.oneTimeShipmentForm.get('destinationCity').setValue(destination.cityName);
            this.oneTimeShipmentForm.get('destinationState').setValue(destination.stateAbbrev);
            this.oneTimeShipmentForm.get('destinationZipId').setValue(destination.zipCode);
          }
        });

    // setting scac on value changes
    this.oneTimeShipmentForm.get('scac').valueChanges.pipe(filter(value => value))
        .pipe(takeWhile(() => this.observer$.value))
        .subscribe(scac => {
          const carrier  = this.carrierList.filter(x => x.scac === scac)[0];
          if (AppConstants.isNotNullCondition(carrier)) {
            this.oneTimeShipmentForm.get('scacName').setValue(carrier.scacName);
            this.oneTimeShipmentForm.get('scacPhone').setValue(carrier.shipmentRptContPhone);
            this.oneTimeShipmentForm.get('scacEmail').setValue(carrier.shipmentRptContEmail);
          }
        });
  }

  /**
   * @description returns the state of form control in any form group based on validity and state.
   */
  isFormControlInValid(formControl: string) {
    const fromElement = this.oneTimeShipmentForm.get(formControl);
    return fromElement && fromElement.invalid &&
        fromElement.touched;
  }

  /**
   * @description gets the list of accessorails from service.
   */
  getAllAccesorials() {
    this.frtAccessorialsMasterService.getAll().pipe(takeWhile(() =>  this.observer$.value)).subscribe(accessorials => {
      this.accessorials = accessorials;
    }, (error) => {
      this.alertService.error('Error while getting accessorials');
    });
  }

  /**
   * @description gets the list of scac for business type HHG
   */
  private getScac() {
    this.loader.show();
    this.surveyService.getFRTScac()
        .pipe(takeWhile(() => this.observer$.value))
        .subscribe((carrierList: any) => {
          if (AppConstants.isNotNullCondition(carrierList)) {
            this.carrierList = carrierList;
            this.scacCodeList = carrierList.map(carrier => carrier.scac);
          }
          this.loader.hide();
        }, error => {
          this.loader.hide();
          this.alertService.error('There was an error retrieving HHG TSP SCAC List');
        });
  }


  createShipmentForm() {
    this.oneTimeShipmentForm = this.fb.group({
      pickupDate: [new Date(), [Validators.required]],
      estimatedCost: [null, [Validators.required, Validators.max(999999.99)]],
      quoteDate: [new Date(), [Validators.required]],
      scacSearchString: [''],
      scac: [''],
      scacName: [''],
      scacPhone: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(20)]],
      scacEmail: ['', [Validators.required, CommonValidators.email, Validators.maxLength(254)]],
      asyncOriginSearchString: ['', [Validators.required]],
      asyncDestinationSearchString: ['', [Validators.required]],
      originId: [''],
      originZipId: ['', [Validators.required]],
      originCity: [''],
      originState: [''],
      originCountry: ['', [Validators.required]],
      destinationId: [''],
      destinationZipId: ['', [Validators.required]],
      destinationCity: [''],
      destinationState: [''],
      destinationCountry: ['', [Validators.required]],
      estimatedMileage: ['', [Validators.required]],
      estimatedWeight: ['', [Validators.required]],
      tenderQuote: [''],
      accessorials: new UntypedFormArray([]),
      comments: ['']
    });
    this.stateCountryService.getAllCountries()
      .pipe(takeWhile(() =>  this.observer$.value))
      .subscribe((countries: any) => {
        this.allCountries = countries;
        const defaultOrigin = this.allCountries.find(x => {
          return x.stateCountryCode === 'US00';
        });
        this.defaultCountry = defaultOrigin;
        this.oneTimeShipmentForm
          .get('originCountry')
          .setValue(this.defaultCountry);
        this.oneTimeShipmentForm
          .get('destinationCountry')
          .setValue(this.defaultCountry);
      });
  }
  onOriginCountryChange() {
    const country = this.oneTimeShipmentForm
      .get('originCountry').value;
    if (country.stateCountryCode === 'US00') {
      this.showOriginLocation = true;
      this.oneTimeShipmentForm.get('originZipId').setValidators([Validators.required]);
      this.oneTimeShipmentForm.get('originZipId').updateValueAndValidity();
      this.oneTimeShipmentForm.get('asyncOriginSearchString').setValidators([Validators.required]);
      this.oneTimeShipmentForm.get('asyncOriginSearchString').updateValueAndValidity();
      this.showEstimatedMileage = true;
      this.oneTimeShipmentForm.get('estimatedMileage').setValidators([Validators.required]);
      this.oneTimeShipmentForm.get('estimatedMileage').updateValueAndValidity();
    } else {
      this.showOriginLocation = false;
      this.oneTimeShipmentForm.get('originZipId').clearValidators();
      this.oneTimeShipmentForm.get('originZipId').updateValueAndValidity();
      this.oneTimeShipmentForm.get('asyncOriginSearchString').clearValidators();
      this.oneTimeShipmentForm.get('asyncOriginSearchString').updateValueAndValidity();
      this.showEstimatedMileage = false;
      this.oneTimeShipmentForm.get('estimatedMileage').clearValidators();
      this.oneTimeShipmentForm.get('estimatedMileage').updateValueAndValidity();
    }
  }
  onDestinationCountryChange() {
    const country = this.oneTimeShipmentForm
      .get('destinationCountry').value;
    if (country.stateCountryCode === 'US00') {
      this.showDestinationLocation = true;
      this.oneTimeShipmentForm.get('destinationZipId').setValidators([Validators.required]);
      this.oneTimeShipmentForm.get('destinationZipId').updateValueAndValidity();
      this.oneTimeShipmentForm.get('asyncDestinationSearchString').setValidators([Validators.required]);
      this.oneTimeShipmentForm.get('asyncDestinationSearchString').updateValueAndValidity();
      this.showEstimatedMileage = true;
      this.oneTimeShipmentForm.get('estimatedMileage').setValidators([Validators.required]);
      this.oneTimeShipmentForm.get('estimatedMileage').updateValueAndValidity();
    } else {
      this.showDestinationLocation = false;
      this.oneTimeShipmentForm.get('destinationZipId').clearValidators();
      this.oneTimeShipmentForm.get('destinationZipId').updateValueAndValidity();
      this.oneTimeShipmentForm.get('asyncDestinationSearchString').clearValidators();
      this.oneTimeShipmentForm.get('asyncDestinationSearchString').updateValueAndValidity();
      this.showEstimatedMileage = false;
      this.oneTimeShipmentForm.get('estimatedMileage').clearValidators();
      this.oneTimeShipmentForm.get('estimatedMileage').updateValueAndValidity();
    }
  }

  changeTypeaheadOriginLoading(e: boolean): void {
    this.typeaheadOriginLoading = e;
  }

  changeTypeaheadDestinationLoading(e: boolean): void {
    this.typeaheadDestinationLoading = e;
  }

  typeaheadOriginOnSelect(e: TypeaheadMatch): void {
    this.oneTimeShipmentForm.get('originId').setValue(e.item);
  }

  typeaheadDestinationOnSelect(e: TypeaheadMatch): void {
    this.oneTimeShipmentForm.get('destinationId').setValue(e.item);
  }

  typeaheadScacOnSelect(e: TypeaheadMatch): void {
    this.oneTimeShipmentForm.get('scac').setValue(e.item);
  }

  /**
   * @description call PCMiler service to get milage
   */
  pcMilerService(originZipId, destinationZipId, businessType, pickUpDate) {
    if (originZipId !== null && destinationZipId !== null && originZipId !== undefined && destinationZipId !== undefined
        && originZipId !== '' && destinationZipId !== '') {
          this.ratesSroMasterService.getMiles(originZipId, destinationZipId, 'FRT', pickUpDate).subscribe(estimatedMileage => {
        this.oneTimeShipmentForm.get('estimatedMileage').patchValue(estimatedMileage, {
          onlySelf: true,
          emitEvent: false
        });
      }, error2 => {
        this.alertService.info('cannot get the mileage between the above origin and destination');
      });
    }
  }


  /**
   * @description adds accessorials form control into the form array
   */
  addAccessorial() {
    (this.oneTimeShipmentForm.get('accessorials') as UntypedFormArray).push(this.createNewAccessorial());
  }

  /**
   * @description creates accessorial form control in the form group array
   */
  createNewAccessorial(): UntypedFormGroup {
    return this.fb.group({
      accessorial: [null, [Validators.required]],
      units: [null, []],
      unitDescription: [null]
    });
  }

  /**
   * @description listens to the changes in accessorials in formarray.
   * @param: event
   * @param: index
   */
  onAccessorialChange(event, index) {
    const accessorial = this.oneTimeShipmentForm.get('accessorials')['controls'][index]['controls'].accessorial.value;
    this.loader.show();
    this.frtAccessorialsMasterService.getUnitsDescriptionById(accessorial.unitsId).subscribe((units: Units) => {
      this.loader.hide();
      if (units !== null &&  units !== undefined) {
        this.oneTimeShipmentForm.get('accessorials')['controls'][index]['controls'].unitDescription.setValue(units.userDisplay);
      }
    }, error => {
      this.loader.hide();
      this.alertService.error('Error while getting units description. please contact system administrator.');
    });
  }

  /**
   * @description removes accessorials form control from the form array at specified index
   * @param: i: index of the form control
   */
  removeAccessorial(i) {
    (this.oneTimeShipmentForm.get(
        'accessorials'
    ) as UntypedFormArray).removeAt(i);
  }

  submitShipment() {
    const formValue = _.cloneDeep(this.oneTimeShipmentForm.value);
    // formValue.estimatedMileage = new RoundPipe().transform(formValue.estimatedMileage);
    const accessorials = [];
    if (AppConstants.isNotNullCondition(formValue.originId)) {
      if (!formValue.originId.uspsZipId) {
        this.ratesSroMasterService.getLocation(formValue.originId.cityName,
            formValue.originId.zipCode, formValue.originId.stateAbbrev).subscribe(response => {
          formValue.originId = response.uspsZipId;
        }, error2 => {
          this.alertService.error('Error while getting the USPS zip ID of origin.');
        });
      } else {
        formValue.originId = formValue.originId.uspsZipId;
      }
    }
    let carrier;
    if (AppConstants.isNotNullCondition(formValue.scac)) {
        carrier = this.carrierList.filter(x => x.scac === formValue.scac)[0];
    }
    formValue.carrierId = (carrier !== null && carrier !== undefined) ? carrier.carrierId : null;
    if (AppConstants.isNotNullCondition(formValue.destinationId)) {
      if (!formValue.destinationId.uspsZipId) {
        this.ratesSroMasterService.getLocation(formValue.destinationId.cityName,
            formValue.destinationId.zipCode, formValue.destinationId.stateAbbrev).subscribe(response => {
          formValue.destinationId = response.uspsZipId;
        }, error2 => {
          this.alertService.error('Error while getting the USPS zip ID of origin.');
        });
      } else {
        formValue.destinationId = formValue.destinationId.uspsZipId;
      }
    }
    formValue.accessorials.forEach(function(element) {
      const obj = {
        frtAccessorialId: element.accessorial.frtAccessorialsId,
        unitsId: element.accessorial.unitsId,
        quantity: element.units === null ? 0 : element.units
      };
      accessorials.push(obj);
    });
    formValue.accessorials = accessorials;
    if (AppConstants.isNotNullCondition(formValue.originCountry)) {
      formValue.originCountryId = formValue.originCountry.stateCountryId;
    }
    if (AppConstants.isNotNullCondition(formValue.destinationCountry)) {
      formValue.destinationCountryId = formValue.destinationCountry.stateCountryId;
    }
    delete formValue.asyncDestinationSearchString;
    delete formValue.asyncOriginSearchString;
    formValue.pickupDate = formValue.pickupDate.toISOString();
    formValue.quoteDate = formValue.quoteDate.toISOString();
    console.log(formValue);
      this.hhgRateQueryService.saveBookShipmentDetailsForOTOBookshipments(formValue).subscribe(bookshipmentid => {
          this.loader.hide();
          if (AppConstants.isNotNullCondition(bookshipmentid)) {
              this.router.navigate(['/', 'frtbookshipment'],
                  {queryParams: {bookshipmentid: bookshipmentid}});
          }
      }, error => {
          this.loader.hide();
          this.alertService.error('Error while saving onetime shipmentInfo');
      });
  }

  resetForm() {
    this.createShipmentForm();
  }

  /**
   * @description formats the date into yyyy-mm-dd format
   * @param date: Jan 01, 0000
   */
  formatDate(date) {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }
    return [year, month, day].join('-');
  }

  setTwoNumberDecimal() {
    const quoteAmt = parseFloat(this.oneTimeShipmentForm.get('estimatedCost').value).toFixed(2);
    // parseFloat(estimatedCost.value.toFixed(2));
    this.oneTimeShipmentForm.get('estimatedCost').setValue(quoteAmt);
    // this.value = parseFloat(this.value).toFixed(2);
   }
      /**
     *  validating for testing purpose only it will return invalid fields
     */
    validateForm() {
        AppConstants.getFormValidationErrors(this.oneTimeShipmentForm);
    }

}
